// biome-ignore-all lint/suspicious/noExplicitAny: We need any here
import type { ReactNode } from 'react'
import { FormattedMessage as FMessage, useIntl as useReactIntl } from 'react-intl'
import { getTranslationDebugSettings, LOCALAZY_SEARCH_URL } from './translationDebug'

type Message<K extends string, W extends string> = {
  id: string
  description?: string
  values?: { [key in K]: string | true }
  wrappers?: { [key in W]: string | true }
}

type ObjectFromList<
  K extends string,
  W extends string,
  T extends readonly (string | Message<K, W>)[],
> = {
  [P in T extends ReadonlyArray<infer U>
    ? U extends Message<K, W>
      ? U['id']
      : U
    : never as P extends string ? P : never]: P extends Extract<T[number], Message<K, W>>['id']
    ? Extract<T[number], { id: P }>
    : { id: P }
}

const listToObject = <
  K extends string,
  W extends string,
  T extends readonly (string | Message<K, W>)[],
>(
  namespace: string,
  list: T
): ObjectFromList<K, W, T> => {
  return list.reduce(
    (acc, key) => {
      if (typeof key === 'object') {
        return {
          ...acc,
          [key.id]: { ...key, id: `${namespace}.${key.id}` },
        }
      }
      return {
        ...acc,
        [key]: {
          id: `${namespace}.${key}`,
        },
      }
    },
    {} as ObjectFromList<K, W, T>
  )
}

export const defineMessages = <
  K extends string,
  W extends string,
  T extends readonly (string | Message<K, W>)[],
>(
  namespace: string,
  messages: T
): ObjectFromList<K, W, T> => {
  return listToObject(namespace, messages)
}

const getDefaultMessage = (
  messageId: string,
  values?: Record<string, any>,
  wrappers?: Record<string, any>,
  useFullId?: boolean
): string => {
  const defaultMessage = useFullId ? messageId : (messageId.split('.').pop() ?? messageId)
  const parts: string[] = [defaultMessage]

  if (values && Object.keys(values).length > 0) {
    parts.push(
      `[${Object.keys(values)
        .map(key => `(${key}): "{${key}}"`)
        .join('; ')}]`
    )
  }

  if (wrappers && Object.keys(wrappers).length > 0) {
    parts.push(
      `[${Object.keys(wrappers)
        .map(key => `<<${key}>${key}</${key}>>`)
        .join('; ')}]`
    )
  }

  return parts.join(' ')
}

const getMissingPlaceholders = (
  rawMessage: string | undefined,
  values?: Record<string, any>,
  wrappers?: Record<string, any>
): string[] => {
  if (!rawMessage) {
    return []
  }
  const missing: string[] = []
  if (values) {
    for (const key of Object.keys(values)) {
      if (!rawMessage.includes(`{${key}}`)) {
        missing.push(`{${key}}`)
      }
    }
  }
  if (wrappers) {
    for (const key of Object.keys(wrappers)) {
      if (!rawMessage.includes(`<${key}>`)) {
        missing.push(`<${key}>`)
      }
    }
  }
  return missing
}

type FormattedMessagePropsSingle<M> =
  M extends Message<infer K, infer W>
    ? M extends { values: any; wrappers: any }
      ? {
          message: M
          values: { [key in K]: ReactNode }
          wrappers: { [key in W]: (text: ReactNode) => ReactNode }
        }
      : M extends { values: any }
        ? {
            message: M
            values: { [key in K]: ReactNode }
            wrappers?: never
          }
        : M extends { wrappers: any }
          ? {
              message: M
              values?: never
              wrappers: { [key in W]: (text: ReactNode) => ReactNode }
            }
          : {
              message: M
              values?: never
              wrappers?: never
            }
    : M extends { id: string; description?: string }
      ? {
          message: M
          values?: never
          wrappers?: never
        }
      : {
          message: { id: string; description?: string }
          values?: never
          wrappers?: never
        }

type FormattedMessageProps<M> = [M] extends [{ id: string; values?: never; wrappers?: never }]
  ? {
      message: M
      values?: never
      wrappers?: never
    }
  : [M] extends [{ id: string; description?: string; values?: undefined; wrappers?: undefined }]
    ? {
        message: M
        values?: never
        wrappers?: never
      }
    : M extends any
      ? FormattedMessagePropsSingle<M>
      : never

export const FormattedMessage = <
  M extends Message<any, any> | { id: string; description?: string },
>(
  props: FormattedMessageProps<M>
): ReactNode => {
  const { message } = props
  const values = {
    ...(props.values ? props.values : {}),
    ...(props.wrappers ? props.wrappers : {}),
  }

  const debugSettings = getTranslationDebugSettings()
  const intl = useReactIntl()

  const hasAnyDebug =
    debugSettings.showKeys || debugSettings.showLocalazyLinks || debugSettings.checkVariables

  if (!hasAnyDebug) {
    return (
      <FMessage
        id={message.id}
        values={values}
        defaultMessage={getDefaultMessage(message.id, props.values, props.wrappers)}
      />
    )
  }

  const missingWarning = debugSettings.checkVariables
    ? (() => {
        const rawMsg = intl.messages[message.id] as string | undefined
        const missing = getMissingPlaceholders(rawMsg, props.values, props.wrappers)
        if (missing.length === 0) {
          return null
        }
        return (
          <span
            className='translation-debug-trigger text-red-600 font-bold ml-1 cursor-help'
            title={`Missing: ${missing.join(', ')}`}
          >
            {'\u26A0'}
          </span>
        )
      })()
    : null

  const localazyLink = debugSettings.showLocalazyLinks ? (
    <a
      href={`${LOCALAZY_SEARCH_URL}${message.id}`}
      target='_blank'
      rel='noopener noreferrer'
      className='translation-debug-trigger text-[10px] uppercase bg-white px-1 py-0.5 rounded border border-indigo-500 text-indigo-500 ml-1 no-underline hover:bg-indigo-50 transition-colors'
    >
      Edit
    </a>
  ) : null

  const hasDebugControls = localazyLink || missingWarning

  const wrapperClassName = hasDebugControls
    ? 'inline rounded px-0.5 transition-all has-[.translation-debug-trigger:hover]:bg-indigo-200 has-[.translation-debug-trigger:hover]:ring-2 has-[.translation-debug-trigger:hover]:ring-indigo-400 has-[.translation-debug-trigger:hover]:shadow-md'
    : ''

  if (debugSettings.showKeys) {
    return (
      <span className={wrapperClassName}>
        {getDefaultMessage(message.id, props.values, props.wrappers, true)}
        {localazyLink}
        {missingWarning}
      </span>
    )
  }

  if (hasDebugControls) {
    return (
      <span className={wrapperClassName}>
        <FMessage
          id={message.id}
          values={values}
          defaultMessage={getDefaultMessage(message.id, props.values, props.wrappers)}
        />
        {localazyLink}
        {missingWarning}
      </span>
    )
  }

  return (
    <FMessage
      id={message.id}
      values={values}
      defaultMessage={getDefaultMessage(message.id, props.values, props.wrappers)}
    />
  )
}

export const useIntl = () => {
  const intl = useReactIntl()

  function formatMessage<K extends string, W extends string>(
    message: Message<K, W> & { values: any; wrappers: any },
    options: {
      values: { [key in K]: string | number }
      wrappers: { [key in W]: (text: string) => string }
    }
  ): string
  function formatMessage<K extends string, W extends string>(
    message: Message<K, W> & { values: any } & { wrappers?: never },
    options: {
      values: { [key in K]: string | number }
    }
  ): string
  function formatMessage<K extends string, W extends string>(
    message: Message<K, W> & { wrappers: any } & { values?: never },
    options: {
      wrappers: { [key in W]: (text: string) => string }
    }
  ): string
  function formatMessage<K extends string, W extends string>(
    message: Message<K, W> & { values?: never; wrappers?: never }
  ): string
  function formatMessage(message: {
    id: string
    description?: string
    values?: never
    wrappers?: never
  }): string
  function formatMessage(message: any, options?: any): string {
    const values = {
      ...(options?.values ? options.values : {}),
      ...(options?.wrappers ? options.wrappers : {}),
    }

    const debugSettings = getTranslationDebugSettings()

    const text = debugSettings.showKeys
      ? getDefaultMessage(message.id, options?.values, options?.wrappers, true)
      : intl.formatMessage(
          {
            id: message.id,
            defaultMessage: getDefaultMessage(message.id, options?.values, options?.wrappers),
          },
          values
        )

    const missingMark = debugSettings.checkVariables
      ? (() => {
          const rawMsg = intl.messages[message.id] as string | undefined
          const missing = getMissingPlaceholders(rawMsg, options?.values, options?.wrappers)
          return missing.length > 0 ? ' [!]' : ''
        })()
      : ''

    const localazyLink = debugSettings.showLocalazyLinks
      ? ` [${LOCALAZY_SEARCH_URL}${message.id}]`
      : ''

    return `${text}${missingMark}${localazyLink}`
  }

  return {
    ...intl,
    formatMessage,
  }
}
